// src/pages/Tickets/TicketSeverityList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Ticket Severities (Ticket_Severity_TBL)
// Performs full CRUD (Read, Create, Update, Delete) via ticketSeverityService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { ticketSeverityService, projectService } from '../../api';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function TicketSeverityList() {
  const [projects, setProjects] = useState([]);

  // Fetch projects to populate the Project ID dropdown options
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketSeverityService, {
    idKey: 'severityID',
    itemName: 'Ticket Severity',
    initialSort: { key: 'sequenceOrder', direction: 'asc' },
  });

  // Schema configuration for Ticket_Severity_TBL
  const severityFields = useMemo(() => [
    {
      name: 'severityID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    {
      name: 'severityName',
      label: 'Severity Name',
      type: 'varchar',
      required: true,
      maxLength: 50,
      placeholder: 'e.g. Low, Medium, High, Critical',
    },
    {
      name: 'sequenceOrder',
      label: 'Sequence Order',
      type: 'integer',
      required: true,
      min: 0,
      placeholder: 'e.g. 1, 2, 3...',
    },
  ], [projects]);

  const columns = [
    { key: 'severityID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? proj.projectName : `Project #${row.projectID}`;
      },
    },
    { key: 'severityName', label: 'Severity Name', sortable: true },
    { key: 'sequenceOrder', label: 'Sequence Order', sortable: true, width: '150px' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Severities</h1>
          <div className="subtitle">
            Configure ticket severity levels and ordering per project.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Severity Level
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="severityID"
        emptyMessage="No severities found."
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortConfig={sortConfig}
        onSort={handleSort}
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {isFormOpen && (
        <TableForm
          fields={severityFields}
          initialValues={selectedRecord || { projectID: '', severityName: '', sequenceOrder: '' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Ticket Severity"
        />
      )}

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Severity Level"
        itemLabel={selectedRecord ? selectedRecord.severityName : 'Ticket Severity'}
        deleting={deleting}
      />
    </div>
  );
}
